import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';

const DEFAULT_CHUNK_SIZE = 1000;

interface Arguments<TApiArgs, TApiResult, TData> {
    apiCall: (args: TApiArgs) => Promise<TApiResult>;
    chunkSize: number;
    processChunks: (chunks: TData[][]) => TApiArgs[];
    serial?: boolean;
}

/** @description Please be careful with the case of fetch including
 * multiple primary keys (e.g. billIds and accountIds).
 * In case that chunked primary key is not provided, the fetch does not fire.
 */
export const callApiInChunks =
    <TApiArgs, TApiResult, TData>({
        apiCall,
        chunkSize = DEFAULT_CHUNK_SIZE,
        processChunks,
        serial = false,
    }: Arguments<TApiArgs, TApiResult, TData>) =>
    async (data: TData[]) => {
        const chunks = pipe(data, array.chunksOf(chunkSize), processChunks);

        if (serial) {
            const promiseChainInit: Promise<TApiResult[]> = Promise.resolve([]);

            return pipe(
                chunks,
                array.reduce(promiseChainInit, async (promiseChain, chunk) => {
                    const chainResults = await promiseChain;
                    const apiResult = await apiCall(chunk);
                    chainResults.push(apiResult);

                    return chainResults;
                })
            );
        }

        const apiPromises = pipe(chunks, array.map(apiCall));
        return Promise.all(apiPromises);
    };
